import { useAbility } from '@casl/react';
import { FmdGoodOutlined, MyLocation } from '@mui/icons-material';
import Bolt from '@mui/icons-material/Bolt';
import CloudIcon from '@mui/icons-material/Cloud';
import OpacityIcon from '@mui/icons-material/Opacity';
import PlaceIcon from '@mui/icons-material/Place';
import SensorIcon from '@mui/icons-material/Sensors';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { blue, purple, yellow } from '@mui/material/colors';
import { useMemo } from 'react';
import elecIcon from 'src/assets/utility/elec.svg';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useDashboard } from 'src/context/dashboardContext';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useGetAllApplicationsQuery } from 'src/services/switch-rest-api/application.service';
import { useSearchGatewaysQuery } from 'src/services/switch-rest-api/gateways.service';
import { useGetAllMetersQuery } from 'src/services/switch-rest-api/meter.service';
import { useSearchSensorsQuery } from 'src/services/switch-rest-api/sensors.service';
import { formatDateTime } from 'src/utils/dateHelper';
import { isAllSelection, toConcreteUtilityType } from 'src/utils/selection';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

interface LocationItem {
  id: string;
  name: string;
  type: 'application' | 'meter' | 'gateway' | 'sensor';
  utility?: UtilityType;
  coordinates?: { latitude: number; longitude: number };
  status?: 'online' | 'offline';
  subtitle?: string;
  alerts?: boolean;
}

interface LocationListWidgetProps {
  onItemClick?: () => void;
  cardContainerProps?: any;
}

const LocationListWidget = ({
  onItemClick,
  cardContainerProps,
}: LocationListWidgetProps = {}) => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const { utility, zone } = useDashboard();

  /* States */

  const appId = useEffectiveApplicationId();
  const appGroupId = useEffectiveApplicationGroupId();
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const isAllAppsSelected = isAllSelection(appId);
  const selectedZoneId =
    !isAllAppsSelected && zone?.applicationId === appId ? zone._id : undefined;

  /* API Calls */

  const {
    data: applications,
    isLoading: isLoadingApps,
    isFetching: isFetchingApps,
  } = useGetAllApplicationsQuery(
    {
      limit: '0',
      applicationGroupId: appGroupId,
      ...(appId != null &&
        !isAllAppsSelected && {
          applicationId: appId,
        }),
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Applications') ||
        isAllGroupsSelected,
    }
  );

  const {
    data: meters,
    isLoading: isLoadingMeters,
    isFetching: isFetchingMeters,
  } = useGetAllMetersQuery(
    {
      limit: 1000,
      ...(appId != null && !isAllAppsSelected
        ? {
            applicationId: appId,
          }
        : {
            applicationGroupId: appGroupId,
          }),
      utilityType: toConcreteUtilityType(utility),
      ...(selectedZoneId != null && {
        zoneId: selectedZoneId,
      }),
      hasLocation: true,
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Meters') ||
        isAllGroupsSelected,
    }
  );

  const {
    data: gateways,
    isLoading: isLoadingGateways,
    isFetching: isFetchingGateways,
  } = useSearchGatewaysQuery(
    {
      ...(appId != null && !isAllAppsSelected
        ? {
            applicationId: appId,
          }
        : {
            applicationGroupId: appGroupId,
          }),
      hasLocation: 'true',
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Gateways') ||
        isAllGroupsSelected,
    }
  );

  const {
    data: sensors,
    isLoading: isLoadingSensors,
    isFetching: isFetchingSensors,
  } = useSearchSensorsQuery(
    {
      ...(appId != null && !isAllAppsSelected
        ? {
            applicationId: appId,
          }
        : {
            applicationGroupId: appGroupId,
          }),
      hasLocation: true,
    },
    {
      skip: appGroupId == null || ability.cannot(ACTIONS.READ, 'Sensors'),
    }
  );

  /* Functions & Functional Components */

  const locationItems: LocationItem[] = useMemo(() => {
    if (isAllGroupsSelected) {
      return [];
    }

    // Show devices (meters, gateways and sensors) for both 'All' and specific application
    const meterItems: LocationItem[] =
      meters?.data?.map((meter) => ({
        id: meter._id,
        name: meter.name,
        type: 'meter' as const,
        utility: meter.utilityType,
        coordinates:
          meter.location?.coordinates?.length === 2
            ? {
                latitude: meter.location.coordinates[1],
                longitude: meter.location.coordinates[0],
              }
            : undefined,
        status: meter.isOnline ? 'online' : 'offline',
        subtitle: `Last: ${formatDateTime(meter.lastSeen)}`,
      })) ?? [];

    const gatewayItems: LocationItem[] =
      gateways?.results?.map((gateway) => ({
        id: gateway._id,
        name: gateway.name ?? 'Gateway',
        type: 'gateway' as const,
        coordinates:
          gateway.location?.coordinates?.length === 2
            ? {
                latitude: gateway.location.coordinates[1],
                longitude: gateway.location.coordinates[0],
              }
            : undefined,
        status: gateway.isOnline ? 'online' : 'offline',
        subtitle: `IMEI: ${gateway.imei}`,
      })) ?? [];

    const sensorItems: LocationItem[] =
      sensors?.data?.map((sensor) => ({
        id: sensor._id,
        name: sensor.name ?? 'Sensor',
        type: 'sensor' as const,
        coordinates:
          sensor.location?.coordinates?.length === 2
            ? {
                latitude: sensor.location.coordinates[1],
                longitude: sensor.location.coordinates[0],
              }
            : undefined,
        subtitle: `Model: ${sensor.model ?? 'N/A'}`,
      })) ?? [];

    const applicationItems: LocationItem[] =
      applications?.data
        ?.filter((app) => app.location?.coordinates?.length === 2)
        .map((app) => ({
          id: app._id,
          name: app.name,
          type: 'application' as const,
          coordinates: {
            latitude: app.location!.coordinates[1],
            longitude: app.location!.coordinates[0],
          },
          subtitle: app.location!.address,
        })) ?? [];

    return [
      ...applicationItems,
      ...meterItems,
      ...gatewayItems,
      ...sensorItems,
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, meters, gateways, sensors, isAllGroupsSelected]);

  const isLoading =
    !isAllGroupsSelected &&
    (isLoadingApps ||
      isLoadingMeters ||
      isLoadingGateways ||
      isLoadingSensors ||
      false);
  const isFetching =
    !isAllGroupsSelected &&
    (isFetchingApps ||
      isFetchingMeters ||
      isFetchingGateways ||
      isFetchingSensors ||
      false);

  const handleItemClick = (item: LocationItem) => {
    if (!item.coordinates) return;

    // Dispatch custom event for map navigation
    const event = new CustomEvent('navigate-to-location', {
      detail: {
        coordinates: item.coordinates,
        zoom: 17,
        itemId: item.id,
        itemType: item.type,
        itemName: item.name,
        openPopup: true,
      },
    });
    window.dispatchEvent(event);

    // Call the optional callback (e.g., to collapse the list on mobile)
    onItemClick?.();
  };

  const getDeviceIcon = (item: LocationItem) => {
    if (item.type === 'meter') {
      // Show utility-specific icons for meters
      switch (item.utility) {
        case UtilityType.ELECTRICITY:
          return (
            <Tooltip title='Electricity'>
              <Avatar
                sx={{
                  bgcolor: yellow[100],
                  width: 33,
                  height: 33,
                  ml: 0,
                  mb: 0,
                }}
              >
                <Bolt style={{ color: yellow[800] }} fontSize={'medium'} />
              </Avatar>
            </Tooltip>
          );
        case UtilityType.WATER:
          return (
            <Tooltip title='Water'>
              <Avatar
                sx={{
                  bgcolor: blue[50],
                  width: 33,
                  height: 33,
                  ml: 0,
                  mb: 0,
                }}
              >
                <OpacityIcon style={{ color: blue[500] }} fontSize={'small'} />
              </Avatar>
            </Tooltip>
          );
        case UtilityType.GAS:
          return (
            <Tooltip title='Gas'>
              <Avatar
                sx={{
                  bgcolor: purple[100],
                  width: 33,
                  height: 33,
                  ml: 0,
                  mb: 0,
                }}
              >
                <CloudIcon style={{ color: purple[800] }} fontSize={'medium'} />
              </Avatar>
            </Tooltip>
          );
        default:
          return elecIcon;
      }
    } else if (item.type === 'gateway') {
      return (
        <Tooltip title='Gateway'>
          <Avatar
            sx={{
              bgcolor: blue[50],
              width: 33,
              height: 33,
              ml: 0,
              mb: 0,
            }}
          >
            <SettingsInputAntennaIcon
              style={{ color: '#269aff' }}
              fontSize={'medium'}
            />
          </Avatar>
        </Tooltip>
      );
    } else if (item.type === 'sensor') {
      return (
        <Tooltip title='Sensor'>
          <Avatar
            sx={{
              bgcolor: '#def5ea',
              width: 33,
              height: 33,
              ml: 0,
              mb: 0,
            }}
          >
            <SensorIcon style={{ color: '#046234' }} fontSize={'medium'} />
          </Avatar>
        </Tooltip>
      );
    } else if (item.type === 'application') {
      return (
        <Tooltip title='Application'>
          <Avatar
            sx={{
              bgcolor: alpha(theme.tokens.brandColors.orange, 0.15),
              width: 33,
              height: 33,
              ml: 0,
              mb: 0,
            }}
          >
            <PlaceIcon
              style={{ color: theme.tokens.brandColors.orange }}
              fontSize={'medium'}
            />
          </Avatar>
        </Tooltip>
      );
    }
    return '';
  };

  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <ListItem key={i} disablePadding>
          <ListItemButton>
            <ListItemAvatar>
              <Skeleton variant='circular' width={40} height={40} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant='text' width='60%' />}
              secondary={<Skeleton variant='text' width='40%' />}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );

  return (
    <WidgetCard
      title='Locations'
      subheader='Applications and devices with coordinates'
      cardContainerProps={cardContainerProps}
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <MyLocation />
        </Box>
      }
      cardContentProps={{
        sx: {
          padding: '0px !important',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {isAllGroupsSelected ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Select a specific application group to view device locations.
          </Typography>
        </Box>
      ) : isLoading ? (
        <List sx={{ width: '100%', overflow: 'auto' }}>{renderSkeleton()}</List>
      ) : locationItems.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            py: 4,
            px: 2,
          }}
        >
          <FmdGoodOutlined
            sx={{
              fontSize: 48,
              color: theme.tokens.colors.mutedForeground,
              opacity: 0.5,
            }}
          />
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            No devices with coordinates found
          </Typography>
        </Box>
      ) : (
        <List
          sx={{
            width: '100%',
            overflow: 'auto',
            maxHeight: '100%',
            padding: 0,
            '& .MuiListItemButton-root:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& > :not(:last-child)': {
              borderBottom: `1px solid ${theme.tokens.colors.border}`,
            },
          }}
        >
          {locationItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleItemClick(item)}
                disabled={isFetching}
                sx={{ py: 0.5 }}
              >
                <ListItemAvatar sx={{ mr: '-12px' }}>
                  {getDeviceIcon(item)}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {item.status && (
                        <Chip
                          color={item.status === 'online' ? 'success' : 'error'}
                          sx={{
                            height: theme.tokens.spacing.sm,
                            width: theme.tokens.spacing.sm,
                          }}
                        />
                      )}
                      <Typography
                        variant='body2'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant='body1'
                      sx={{
                        fontSize: theme.tokens.typography.fontSize.xs,
                        color: theme.tokens.colors.mutedForeground,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {item.subtitle}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </WidgetCard>
  );
};

export default LocationListWidget;
